<template>
    <div class="flex flex-col center-box relative">
        <Loading v-if="loading"  class="absolute top-0 w-full"/>
        <div v-if="playlist" class="transition-all transition-duration w-full">
            <PageHeader :showBack="false" @back="$router.back()">
                <template #title>
                    <div class="relative flex items-center mt-2 min-w-0 w-full max-w-full">
                        <span class="text-xl sm:text-2xl font-bold dark:text-white truncate block min-w-0 w-full" :title="name" :contenteditable="playlist.permissions && playlist.permissions.includes('editor')" 
                            @blur="updatePlaylistName($event)">{{name}}</span>
                    </div>
                </template>
                <template #subtitle>
                    <div class="pt-1 flex flex-col gap-1.5 text-xs text-gray-500 dark:text-gray-400 min-w-0 w-full">
                        <div class="flex flex-wrap items-center gap-1.5 font-medium shrink-0">
                            <span>{{ playlist.songs?.length || 0 }} {{ $t('playlistView.songs') }}</span>
                            <span>&middot;</span>
                            <span>{{ estimatedTime }} {{ $t('playlistView.estimated') }}</span>
                            <template v-if="timeAgo">
                                <span>&middot;</span>
                                <span>{{ $t('playlistView.editedAgo', { time: timeAgo }) }}</span>
                            </template>
                        </div>
                        <div v-if="playlist.tags && playlist.tags.length > 0" class="min-w-0 max-w-full">
                            <TagsViewer 
                                :tags="playlist.tags || []" 
                                :visibleAddButton="playlist.permissions && playlist.permissions.includes('editor')"
                                @createTag="openTagsEditor" 
                                :element="playlist"
                            />
                        </div>
                    </div>
                </template>
                
                <div class="flex flex-row gap-2 items-center">
                    <!-- Desktop & Tablet Buttons -->
                    <section class="hidden sm:flex flex-row flex-nowrap justify-end gap-1.5 md:gap-2 items-center">
                        <template v-if="playlist.permissions && playlist.permissions.includes('editor')">
                            <Tooltip position="bottom" :text="$t('tooltip.insertCoverImage') || $t('playlistView.cover')"> 
                                <button @click="openImageSearch()" class="h-9 flex flex-shrink-0 items-center justify-center gap-1.5 cursor-pointer px-2.5 xl:px-3.5 text-sm font-semibold rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                                    <PhotoIcon class="w-4 h-4" />
                                    <span class="hidden xl:inline">{{ $t('playlistView.cover') }}</span>
                                </button>
                            </Tooltip>
                            <Tooltip position="bottom" :text="$t('tooltip.share') || $t('playlistView.share')"> 
                                <button @click="openShareModal()" class="h-9 flex flex-shrink-0 items-center justify-center gap-1.5 cursor-pointer px-2.5 xl:px-3.5 text-sm font-semibold rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                                    </svg>
                                    <span class="hidden xl:inline">{{ $t('playlistView.share') }}</span>
                                </button>
                            </Tooltip>
                            <Tooltip position="bottom" :text="$t('tooltip.editorMode') || $t('playlistView.editorMode')"> 
                                <button @click="openEditorMode()" class="h-9 flex flex-shrink-0 items-center justify-center gap-1.5 cursor-pointer px-2.5 xl:px-3.5 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
                                    <BookOpenIcon class="w-4 h-4" />
                                    <span class="hidden xl:inline">{{ $t('playlistView.editorMode') }}</span>
                                </button>
                            </Tooltip>
                        </template>

                        <!-- Offline Download Button -->
                        <div class="relative" ref="offlineMenuRef">
                            <Tooltip position="bottom" :text="isOfflineDownloaded ? $t('playlistView.offlineReady') : $t('playlistView.saveOffline')">
                                <button 
                                    @click="toggleOfflineMenu" 
                                    :disabled="isOfflineDownloading"
                                    class="h-9 flex flex-shrink-0 items-center justify-center gap-1.5 cursor-pointer px-2.5 xl:px-3.5 text-sm font-semibold rounded-lg border transition-colors shadow-sm"
                                    :class="[
                                        isOfflineDownloaded 
                                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60'
                                            : isOfflineDownloading
                                                ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300 cursor-wait'
                                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    ]"
                                >
                                    <template v-if="isOfflineDownloading">
                                        <ArrowPathIcon class="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                                        <span class="hidden xl:inline">{{ $t('playlistView.downloading', { current: offlineProgress.current, total: offlineProgress.total || playlist.songs?.length || 0 }) }}</span>
                                        <span class="xl:hidden text-xs font-semibold">{{ offlineProgress.percentage }}%</span>
                                    </template>
                                    <template v-else-if="isOfflineDownloaded">
                                        <CheckCircleIcon class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                        <span class="hidden xl:inline">{{ $t('playlistView.offlineReady') }}</span>
                                    </template>
                                    <template v-else>
                                        <CloudArrowDownIcon class="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                        <span class="hidden xl:inline">{{ $t('playlistView.saveOffline') }}</span>
                                    </template>
                                </button>
                            </Tooltip>

                            <!-- Dropdown for Offline Actions when Downloaded -->
                            <div v-if="isOfflineMenuOpen" class="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1.5 z-50 overflow-hidden">
                                <div class="px-3 py-2 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium">
                                    {{ $t('playlistView.offlineNotice') }}
                                </div>
                                <button @click="startOfflineDownload(); isOfflineMenuOpen = false" class="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2">
                                    <ArrowPathIcon class="w-4 h-4 text-indigo-500" />
                                    <span>{{ $t('playlistView.updateOffline') }}</span>
                                </button>
                                <button @click="removeOffline(); isOfflineMenuOpen = false" class="w-full text-left px-4 py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2">
                                    <TrashIcon class="w-4 h-4 text-red-500" />
                                    <span>{{ $t('playlistView.removeOffline') }}</span>
                                </button>
                            </div>
                        </div>

                        <!-- Open in Studio Button -->
                        <Tooltip position="bottom" :text="$t('playlistView.openInStudioTooltip') || 'Abrir no Studio'"> 
                            <button @click="openInStudio()" class="h-9 flex flex-shrink-0 items-center justify-center gap-1.5 cursor-pointer px-2.5 xl:px-3.5 text-sm font-semibold rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-violet-500">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                                </svg>
                                <span class="hidden xl:inline">{{ $t('playlistView.openInStudio') || 'Abrir no Studio' }}</span>
                            </button>
                        </Tooltip>

                        <!-- Play Button -->
                        <Tooltip position="bottom" :text="$t('tooltip.play') || $t('playlistView.play') || 'Play'"> 
                            <button @click="playPlaylist()" class="h-9 flex flex-shrink-0 items-center gap-1.5 cursor-pointer px-3.5 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm">
                                <PlayIcon class="w-4 h-4" />
                                <span>{{ $t('playlistView.play') || 'Play' }}</span>
                            </button>
                        </Tooltip>
                    </section>

                    <!-- Mobile Actions Dropdown -->
                    <div class="sm:hidden relative">
                        <button
                            @click="isMobileActionsOpen = !isMobileActionsOpen"
                            type="button"
                            class="flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm active:scale-95 transform duration-150"
                            title="Quick Actions"
                        >
                            <Squares2X2Icon class="w-5 h-5 text-indigo-500" />
                        </button>
                        
                        <!-- Bottom Sheet Menu -->
                        <Transition name="bottom-sheet">
                            <div v-if="isMobileActionsOpen" class="fixed inset-0 z-[150] flex flex-col justify-end sm:hidden">
                                <!-- Backdrop -->
                                <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" @click="isMobileActionsOpen = false"></div>
                                
                                <!-- Panel -->
                                <div class="relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl p-6 pb-8 max-h-[80vh] overflow-y-auto w-full mx-auto z-10 border-t border-gray-100 dark:border-gray-700">
                                    <!-- Handle -->
                                    <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-5"></div>
                                    
                                    <!-- Header -->
                                    <div class="flex items-start justify-between mb-6">
                                        <div>
                                            <h3 class="text-lg font-bold text-gray-900 dark:text-white">Quick Actions</h3>
                                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Manage your playlist</p>
                                        </div>
                                        <button @click="isMobileActionsOpen = false" class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                                            <XMarkIcon class="size-5" />
                                        </button>
                                    </div>

                                    <!-- Buttons grid -->
                                    <div class="grid grid-cols-3 gap-4">
                                        <!-- Play -->
                                        <div class="flex flex-col items-center text-center cursor-pointer" @click="playPlaylist(); isMobileActionsOpen = false">
                                            <div class="flex items-center justify-center p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl hover:bg-emerald-100 dark:hover:bg-emerald-800/40 transition-all shadow-sm border border-emerald-100 dark:border-emerald-800/50 w-full mb-2">
                                                <PlayIcon class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                            <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">{{ $t('playlistView.play') || 'Play' }}</span>
                                        </div>

                                        <!-- Offline Download (Mobile) -->
                                        <div class="flex flex-col items-center text-center cursor-pointer" @click="handleMobileOfflineAction">
                                            <div class="flex items-center justify-center p-4 rounded-2xl transition-all shadow-sm border w-full mb-2"
                                                :class="[
                                                    isOfflineDownloaded
                                                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800'
                                                        : isOfflineDownloading
                                                            ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800'
                                                            : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/30'
                                                ]"
                                            >
                                                <ArrowPathIcon v-if="isOfflineDownloading" class="w-6 h-6 text-indigo-500 animate-spin" />
                                                <CheckCircleIcon v-else-if="isOfflineDownloaded" class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                                <CloudArrowDownIcon v-else class="w-6 h-6 text-sky-500" />
                                            </div>
                                            <span class="text-xs text-gray-600 dark:text-gray-300 font-medium truncate w-full">
                                                {{ isOfflineDownloading ? `${offlineProgress.percentage}%` : (isOfflineDownloaded ? $t('playlistView.offlineReady') : $t('playlistView.saveOffline')) }}
                                            </span>
                                        </div>

                                        <!-- Open in Studio (Mobile) -->
                                        <div class="flex flex-col items-center text-center cursor-pointer" @click="openInStudio(); isMobileActionsOpen = false">
                                            <div class="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-all shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-violet-500">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                                                </svg>
                                            </div>
                                            <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">{{ $t('playlistView.openInStudio') || 'Abrir no Studio' }}</span>
                                        </div>

                                        <template v-if="playlist.permissions && playlist.permissions.includes('editor')">
                                            <div class="flex flex-col items-center text-center cursor-pointer" @click="openEditorMode(); isMobileActionsOpen = false">
                                                <div class="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-2">
                                                    <BookOpenIcon class="w-6 h-6 text-emerald-500" />
                                                </div>
                                                <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">{{ $t('playlistView.editorMode') }}</span>
                                            </div>

                                            <div class="flex flex-col items-center text-center cursor-pointer" @click="openImageSearch(); isMobileActionsOpen = false">
                                                <div class="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-purple-500">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                                                    </svg>
                                                </div>
                                                <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">{{ $t('playlistView.cover') }}</span>
                                            </div>

                                            <div class="flex flex-col items-center text-center cursor-pointer" @click="openShareModal(); isMobileActionsOpen = false">
                                                <div class="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-indigo-500">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                                                    </svg>
                                                </div>
                                                <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">{{ $t('playlistView.share') }}</span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </PageHeader>
            <div class="px-4 pb-4 w-full">
                <TriadTagsEditor ref="tagsEditor" />
                <ModalShare ref="shareModal" v-if="playlist.permissions && playlist.permissions.includes('editor')"/>
                <ImageSearchModal ref="imageSearchModal" :id="playlist._id" :playlist="playlist" />
            </div>
            <div class="flex-grow w-full p-2 flex flex-col-reverse lg:flex-row gap-4">
                <SongTable class="w-full lg:w-2/3 xl:w-3/4" @sorted="onSort()" @reload="reloadTable()" :isEditor="playlist.permissions && playlist.permissions.includes('editor')" :playlist="playlist"/>
                
                <div class="w-full lg:w-1/3 xl:w-1/4 flex flex-col gap-4">
                    <!-- Image Card -->
                    <PlaylistImageCard
                        :playlist="playlist"
                        :suggested-image="suggestedImage"
                        :suggested-image-loading="suggestedImageLoading"
                        @open-image-search="openImageSearch"
                        @refresh-suggested="refreshSuggestedImage"
                        @save-suggested="saveSuggestedImage"
                        @remove-image="removeImage"
                    />

                    <!-- Description Card -->
                    <PlaylistDescriptionCard
                        :playlist="playlist"
                        :is-saving="isSavingDescription"
                        @save-description="saveDescription"
                    />

                    <!-- Tags Card -->
                    <TagListViewer v-if="computedSongTags.length > 0" :tags="computedSongTags" />
                </div>
            </div> 
            <div class="flex justify-end py-8 px-4 w-full" v-if="playlist.permissions && playlist.permissions.includes('editor')">
                <div id="delete-playlist" class="col-start-8">
                    <Tooltip position="bottom" :text="$t('tooltip.deletePlaylist')">
                        <button @click="deletePlaylist()" class="flex items-center justify-center gap-2 cursor-pointer px-4 py-2 text-sm font-semibold bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg hover:from-red-600 hover:to-rose-600 transition-colors shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                            <span>Delete</span>
                        </button>
                    </Tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import playlistStore from '@/store/playlist.store'
import sideMenuStore from '@/store/sidemenu.store'
import shareModalStore from '@/store/share.modal.store'
import { realtimeService } from '@/service/realtime.service'
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import userStore from '@/store/user.store'
import appStore from "@/store/app.store";
import playlistService from "@/service/playlist.service";
import Tooltip from '@/components/common/elements/TriadTooltip.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import {BookOpenIcon, PlayIcon} from '@heroicons/vue/24/solid';
import PlaylistEditor from '@/components/playlist/PlaylistEditor.vue';
import { openPlaylistInStudio } from '@/utils/studio';
import TagsViewer from '@/components/common/elements/TagsViewer.vue';
import TriadTagsEditor from '@/components/common/TriadTagsEditor.vue';
import TagListViewer from '@/components/common/elements/TagListViewer.vue';
import PlaylistImageCard from '@/components/playlist/PlaylistImageCard.vue';
import PlaylistDescriptionCard from '@/components/playlist/PlaylistDescriptionCard.vue';
import ImageSearchModal from '@/components/common/ImageSearchModal.vue';
import { PhotoIcon } from '@heroicons/vue/24/outline';
import { Squares2X2Icon, XMarkIcon } from '@heroicons/vue/24/outline';
import { appEvents } from '@/store/app.events';
import { ArrowPathIcon, CloudArrowDownIcon, CheckCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import offlinePlaylistService from '@/service/offlinePlaylist.service';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const isMobileActionsOpen = ref(false);

const playlist = ref(null)
const shareModal = ref(null)
const route = useRoute()
const router = useRouter();
const currentId = ref(null);
let playlistRoom = null;
const name = ref(null);

const loading = ref(false);

// Offline playlist states
const isOfflineDownloaded = ref(false);
const isOfflineDownloading = ref(false);
const isOfflineMenuOpen = ref(false);
const offlineMenuRef = ref(null);
const offlineProgress = ref({ current: 0, total: 0, percentage: 0, songTitle: '' });

const estimatedTime = computed(() => {
    if (!playlist.value || !playlist.value.songs) return '0 min';
    // Estimar 4 minutos por música
    const totalMinutes = playlist.value.songs.length * 4;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
});

const timeAgo = computed(() => {
    if (!playlist.value || !playlist.value.updatedAt) return '';
    const date = new Date(playlist.value.updatedAt);
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " " + t('timeAgo.years');
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " " + t('timeAgo.months');
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " " + t('timeAgo.days');
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " " + t('timeAgo.hours');
    interval = seconds / 60;
    if (interval >= 1) return Math.floor(interval) + " " + t('timeAgo.minutes');
    return Math.floor(seconds) + " " + t('timeAgo.seconds');
});

const computedSongTags = computed(() => {
    if (!playlist.value || !playlist.value.songs) return [];
    
    const counts = {};
    const tagsObj = {};
    
    const playlistTagsMap = {};
    if (playlist.value.tags) {
        playlist.value.tags.forEach(t => {
            if (typeof t === 'object' && t !== null && t.name) {
                playlistTagsMap[t.name.trim().toLowerCase()] = t;
            }
        });
    }

    playlist.value.songs.forEach(song => {
        let metadataChanged = song.songMetadataChanged || song.song_metadata_changed;
        if (typeof metadataChanged === 'string') {
            try { metadataChanged = JSON.parse(metadataChanged); } catch (e) {}
        }
        
        let tagsToUse = [];
        if (metadataChanged && Array.isArray(metadataChanged.tags) && metadataChanged.tags.length > 0) {
            tagsToUse = metadataChanged.tags;
        } else {
            tagsToUse = song.data?.tags || song.tags || [];
        }

        const songSeenTags = new Set();
        
        tagsToUse.forEach(t => {
            const rawName = typeof t === 'object' && t !== null ? t.name : t;
            if (!rawName || typeof rawName !== 'string') return;
            const name = rawName.trim();
            if (!name || name.toLowerCase() === 'unknown') return;
            
            const lowerName = name.toLowerCase();
            if (songSeenTags.has(lowerName)) return;
            songSeenTags.add(lowerName);
            
            if (!counts[lowerName]) {
                counts[lowerName] = 0;
                if (playlistTagsMap[lowerName]) {
                    tagsObj[lowerName] = { ...playlistTagsMap[lowerName], name: playlistTagsMap[lowerName].name || name };
                } else {
                    tagsObj[lowerName] = typeof t === 'object' && t !== null ? t : { name };
                }
            }
            counts[lowerName]++;
        });
    });
    
    return Object.keys(counts).map(lowerName => ({
        ...tagsObj[lowerName],
        amount: counts[lowerName]
    }));
});

const CHANNELS_STORAGE_KEY = 'notification_channels';

const saveChannelToStorage = (channelName, eventName = '*') => {
    try {
        const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]');
        const idx = saved.findIndex(s => s.channel === channelName);
        if (idx === -1) {
            saved.push({ channel: channelName, event: eventName });
            localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(saved));
        }
    } catch (e) {
        console.error('Error saving channel to localStorage:', e);
    }
}

const removeChannelFromStorage = (channelName) => {
    try {
        const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]');
        const filtered = saved.filter(s => s.channel !== channelName);
        localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(filtered));
    } catch (e) {
        console.error('Error removing channel from localStorage:', e);
    }
}

const onAppOnline = () => {
    if (currentId.value) {
        loadPlaylist(currentId.value, true);
        if (playlistRoom) {
            realtimeService.unsubscribe("playlist::room::" + currentId.value);
            playlistRoom = null;
        }
        playlistRoom = realtimeService.subscribe(
            "playlist::room::" + currentId.value,
            '*',
            (payload) => onEvent({ event: payload.event, data: payload }),
            (status) => {
                if (status === 'SUBSCRIBED') {
                    saveChannelToStorage("playlist::room::" + currentId.value);
                }
            }
        );
    }
};

const checkOfflineStatus = async () => {
    if (!currentId.value) return;
    try {
        isOfflineDownloaded.value = await offlinePlaylistService.isPlaylistDownloaded(currentId.value);
    } catch (e) {
        console.warn('Error checking offline status:', e);
    }
};

const toggleOfflineMenu = () => {
    if (isOfflineDownloaded.value) {
        isOfflineMenuOpen.value = !isOfflineMenuOpen.value;
    } else {
        startOfflineDownload();
    }
};

const startOfflineDownload = async () => {
    if (!playlist.value) return;
    isOfflineDownloading.value = true;
    isOfflineMenuOpen.value = false;
    try {
        await offlinePlaylistService.downloadPlaylist(playlist.value, (progress) => {
            offlineProgress.value = progress;
        });
        isOfflineDownloaded.value = true;
        toastStore.dispatch("addMessage", { message: t('playlistView.downloadSuccess'), icon: 'success' });
    } catch (e) {
        console.error('Failed to download playlist for offline:', e);
        if (e.code === 'FREE_LIMIT_REACHED' || e.message === 'FREE_LIMIT_REACHED') {
            toastStore.dispatch("addMessage", { 
                message: t('playlistView.offlineFreeLimitReached') || 'Usuários do plano gratuito podem salvar apenas 1 playlist offline.', 
                icon: 'warning' 
            });
        } else {
            toastStore.dispatch("addMessage", { message: 'Erro ao baixar playlist offline', icon: 'error' });
        }
    } finally {
        isOfflineDownloading.value = false;
    }
};

const removeOffline = async () => {
    if (!currentId.value) return;
    try {
        await offlinePlaylistService.removePlaylist(currentId.value);
        isOfflineDownloaded.value = false;
        isOfflineMenuOpen.value = false;
        toastStore.dispatch("addMessage", { message: t('playlistView.removeSuccess'), icon: 'success' });
    } catch (e) {
        console.error('Failed to remove offline playlist:', e);
    }
};

const handleMobileOfflineAction = () => {
    isMobileActionsOpen.value = false;
    if (isOfflineDownloaded.value) {
        isOfflineMenuOpen.value = true;
    } else {
        startOfflineDownload();
    }
};

const onOfflineMenuClickOutside = (event) => {
    if (offlineMenuRef.value && !offlineMenuRef.value.contains(event.target)) {
        isOfflineMenuOpen.value = false;
    }
};

const onOfflinePlaylistDownloaded = ({ playlistId }) => {
    if (playlistId === currentId.value) {
        isOfflineDownloaded.value = true;
    }
};

const onOfflinePlaylistRemoved = ({ playlistId }) => {
    if (playlistId === currentId.value) {
        isOfflineDownloaded.value = false;
    }
};

onMounted( () => {
    init();
    checkOfflineStatus();
    document.addEventListener('click', onOfflineMenuClickOutside);
    appEvents.on('playlistUpdated', onPlaylistUpdated);
    appEvents.on('app:online', onAppOnline);
    appEvents.on('offline:playlist-downloaded', onOfflinePlaylistDownloaded);
    appEvents.on('offline:playlist-removed', onOfflinePlaylistRemoved);
})

onUnmounted( () => {
    if (playlistRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value);
    }
    document.removeEventListener('click', onOfflineMenuClickOutside);
    appEvents.off('playlistUpdated', onPlaylistUpdated);
    appEvents.off('app:online', onAppOnline);
    appEvents.off('offline:playlist-downloaded', onOfflinePlaylistDownloaded);
    appEvents.off('offline:playlist-removed', onOfflinePlaylistRemoved);
})

const loadPlaylist = async (id, force = false) => {
    if (!id) return;
    
    // 1. SWR Instant Display: Check local IndexedDB / memory cache first (0ms)
    let hasCached = false;
    try {
        const cached = await playlistService.getCachedPlaylist(id);
        if (cached && (String(cached.id || cached._id) === String(id))) {
            hasCached = true;
            playlist.value = cached;
            playlistStore.state.playlist = cached;
            name.value = cached.name;
            checkOfflineStatus();
            loading.value = false;
        }
    } catch (e) {
        console.warn('[PlaylistView] SWR cache lookup error:', e);
    }

    if (!hasCached) {
        loading.value = true;
    }

    // 2. Background / Network revalidation
    try {
        const data = await playlistService.fetchPlaylist({ id, force });
        if (data && !data.error) {
            playlist.value = data;
            playlistStore.state.playlist = data;
            name.value = data.name;
            checkOfflineStatus();
        }
    } catch (e) {
        // If we did not have cached data, route to error page; otherwise stay on offline copy
        if (!hasCached) {
            appStore.state.error = e.status || 500;
            appStore.state.message = e.message || "Error loading playlist";
            router.push({ path: "/error" });
        } else {
            console.warn('[PlaylistView] Network fetch failed, continuing with offline cached playlist:', e);
        }
    } finally {
        loading.value = false;
    }
}

const init = () => {
    if (playlistRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value);
    }
    
    playlistStore.dispatch("clean");
    
    currentId.value = route.params.id
    playlist.value = null
    loadPlaylist(currentId.value)

    playlistRoom = realtimeService.subscribe(
        "playlist::room::"+currentId.value,
        '*',
        (payload) => onEvent({event: payload.event, data: payload}),
        (status) => {
            if (status === 'SUBSCRIBED') {
                saveChannelToStorage("playlist::room::"+currentId.value);
            }
        }
    )
}

watch( () => playlistStore.state.error, () => {
    if (playlistStore.state.error){
        toastStore.dispatch("addMessage", {message: playlistStore.state.message, icon: 'error'});
    } else {
        toastStore.dispatch("addMessage", {message: playlistStore.state.message, icon: 'success'});
    }
})

const openShareModal = () => {
    shareModalStore.dispatch("open", route.params.id);
}

watch(
  () => route.params.id,
  (newId) => {
    playlistStore.dispatch("clean");
    currentId.value = newId
    playlist.value = null
    loadPlaylist(newId)
  }
)

const onSort = () => {
    loadPlaylist(currentId.value, true)
}

const reloadTable = () => {
    loadPlaylist(currentId.value, true)
}

const onPlaylistUpdated = (playlistId) => {
    if (currentId.value === playlistId) {
        reloadTable();
    }
}

const deletePlaylist = async () => {
    if(playlist.value.permissions.includes('editor')){
        if(await dialogStore.dispatch('confirm', "Are you sure you want to DELETE the playlist?")){
            playlistStore.dispatch("delete", route.params.id).then(() => {
                playlistStore.state.forceUpdate=true
                sideMenuStore.dispatch("reloadPlaylists")
            })
        }
    }
}

const playlistUpdateOrder = async (update) => {
    if (update){
        if (await dialogStore.dispatch('confirm', "The playlist was sorted. Would like to update?")){
            loadPlaylist(currentId.value, true)
            toastStore.dispatch("addMessage", {message: 'Playlist order updated', icon: 'success'});
        }
    }
}

let lastPlaylistSnapshotTsView = 0;
const onEvent = (state) => {
    if (!state) return;
    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    if (currentUserId && state.data && currentUserId === state.data.sender){
        return;
    }
    if (window.myClientId && state.data && state.data.sender_client_id === window.myClientId) {
        return;
    }
    // Snapshot via transport (Fase 2b: palco offline). Silencioso; mantém tabela e store.
    if (state.event === 'playlist-update-snapshot' && state.data && state.data.playlist) {
        const snap = state.data.playlist;
        const pid = state.data.playlistId || snap.id || snap._id;
        if (pid && String(pid) !== String(currentId.value)) return;
        if (state.data.ts && lastPlaylistSnapshotTsView > state.data.ts) return;
        lastPlaylistSnapshotTsView = state.data.ts || Date.now();
        playlistStore.commit('APPLY_PLAYLIST_SNAPSHOT', { playlist: snap });
        if (playlist.value && String(playlist.value._id || playlist.value.id) === String(pid)) {
            playlist.value = playlistStore.state.playlist;
            name.value = playlistStore.state.playlist?.name;
        }
        return;
    }
    if(/^playlist-update\:\:[0-9a-f]{24}\:\:order$/.test(state.event)) {
        if (state.data) {
            playlistUpdateOrder(state.data.update)
        }
        return
    }
}

const updatePlaylistName = async (event) => {
    if(await dialogStore.dispatch('confirm', "Are you sure you want to update the playlist name?")){
        playlist.value.name = event.target.innerText
        playlistService.updateName(playlist.value._id, playlist.value.name).then(() => {
            toastStore.dispatch("addMessage", {message: 'Playlist name updated', icon: 'success'});
        })
        .catch(() => {
            toastStore.dispatch("addMessage", {message: 'Failed to update playlist name', icon: 'error'});
        })
    } else {
        event.target.innerText = playlist.value.name
    }
    // playlist.value.name = event.target.innerText
    // playlistStore.dispatch("update", playlist.value)
}

const openEditorMode = () => {
    router.push({path: `/playlist/${route.params.id}/editor`})
}

const openInStudio = () => {
    const playlistId = route.params.id || playlist.value?._id || playlist.value?.id;
    openPlaylistInStudio(playlistId);
}

const playPlaylist = () => {
    if (playlist.value && playlist.value.songs && playlist.value.songs.length > 0) {
        router.push({path: `/playlist/${route.params.id}/song`})
    } else {
        toastStore.dispatch('addMessage', {message: 'Playlist has no songs', icon: 'warning'});
    }
}

const tagsEditor = ref(null);

const openTagsEditor = () => {
    if (playlist.value.permissions && playlist.value.permissions.includes('editor')) {
        const aiContext = {
            type: 'playlist',
            name: name.value,
            description: playlist.value.description
        };
        tagsEditor.value.open(playlist.value.tags || [], aiContext).then((updatedTags) => {
            playlistService.updateTags(playlist.value._id, updatedTags).then(() => {
                playlist.value.tags = updatedTags;
                toastStore.dispatch("addMessage", {message: 'Tags updated successfully', icon: 'success'});
            }).catch(() => {
                toastStore.dispatch("addMessage", {message: 'Failed to update tags', icon: 'error'});
            });
        });
    }
}

const imageSearchModal = ref(null);
const suggestedImage = ref(null);
const suggestedImageLoading = ref(false);
const searchPage = ref(1);


const openImageSearch = (initialTab = null) => {
    const tab = typeof initialTab === 'string' ? initialTab : null;
    imageSearchModal.value.open(tab).then((imageUrl) => {
        if (imageUrl) {
            const pid = playlist.value._id || playlist.value.id;
            playlistService.updateImage(pid, imageUrl).then(async (res) => {
                if (res && !res.ok) {
                    throw new Error('Failed to update cover image');
                }
                let savedCover = imageUrl;
                if (res && typeof res.json === 'function') {
                    try {
                        const data = await res.json();
                        if (data && data.cover) {
                            savedCover = data.cover;
                        }
                    } catch (_) {}
                }
                playlist.value.cover = savedCover;
                if (playlistStore.state.playlist) {
                    playlistStore.state.playlist.cover = savedCover;
                }
                suggestedImage.value = null; // Clear suggested image if manual selection is made
                toastStore.dispatch("addMessage", {message: 'Cover image updated', icon: 'success'});
            }).catch(() => {
                toastStore.dispatch("addMessage", {message: 'Failed to update cover image', icon: 'error'});
            });
        }
    });
}

const fetchSuggestedImage = async () => {
    if (!playlist.value || !playlist.value.tags || playlist.value.tags.length === 0 || playlist.value.cover) {
        return;
    }

    suggestedImageLoading.value = true;
    const query = playlist.value.tags.map(tag => tag.name).join(' ');
    try {
        const response = await fetch(`/api/media/search?q=${encodeURIComponent(query)}&per_page=1&page=${searchPage.value}`);
        if (!response.ok) throw new Error('Search failed');
        
        const data = await response.json();
        if (data.photos && data.photos.length > 0) {
            // Prefer large2x, fallbacks
            const img = data.photos[0];
            suggestedImage.value = img.src.large2x || img.src.large || img.src.medium;
        } else {
            suggestedImage.value = null;
        }
    } catch (error) {
        console.error("Failed to fetch suggested image", error);
    } finally {
        suggestedImageLoading.value = false;
    }
}

const saveSuggestedImage = (event) => {
    event.stopPropagation(); // Prevent opening modal
    if (!suggestedImage.value) return;

    const pid = playlist.value._id || playlist.value.id;
    playlistService.updateImage(pid, suggestedImage.value).then(async (res) => {
        if (res && !res.ok) {
            throw new Error('Failed to save cover image');
        }
        playlist.value.cover = suggestedImage.value;
        if (playlistStore.state.playlist) {
            playlistStore.state.playlist.cover = suggestedImage.value;
        }
        suggestedImage.value = null;
        toastStore.dispatch("addMessage", {message: 'Cover image saved', icon: 'success'});
    }).catch(() => {
        toastStore.dispatch("addMessage", {message: 'Failed to save cover image', icon: 'error'});
    });
}

const refreshSuggestedImage = (event) => {
    event.stopPropagation(); // Prevent opening modal
    searchPage.value++;
    fetchSuggestedImage();
}

const isSavingDescription = ref(false);

const saveDescription = () => {
    if (playlist.value.description) {
        isSavingDescription.value = true;
        playlistService.updateDescription(playlist.value._id || playlist.value.id, playlist.value.description).then(() => {
            toastStore.dispatch("addMessage", {message: 'Description saved', icon: 'success'});
        }).catch(() => {
            toastStore.dispatch("addMessage", {message: 'Failed to save description', icon: 'error'});
        }).finally(() => {
            isSavingDescription.value = false;
        })
    }
}

const removeImage = () => {
    playlist.value.cover = null;
    playlistService.updatePlaylist(playlist.value).then(() => {
        toastStore.dispatch("addMessage", {message: 'Cover image removed', icon: 'success'});
    }).catch(() => {
        toastStore.dispatch("addMessage", {message: 'Failed to remove cover image', icon: 'error'});
    });
}

// Watch playlist changes to trigger auto-search
watch(() => playlist.value, (newVal) => {
    if (newVal && !newVal.image && newVal.tags && newVal.tags.length > 0 && !suggestedImage.value) {
        fetchSuggestedImage();
    }
}, { deep: true, immediate: true });

// Also watch tags specifically if they are updated separately (though deep watch on playlist should cover it)
 watch(() => playlist.value?.tags, (newTags) => {
    if (newTags && newTags.length > 0 && !playlist.value.image) {
         // Reset page and search again if tags change and no image
         searchPage.value = 1;
         fetchSuggestedImage();
    }
});


</script>